/**
 * @module Image - Hydrogen Image component
 * @module Link - Hydrogen Link component
 * @module useCallback - React Hook for memoizing callbacks
 * @module useEffect - React Hook for side effects
 * @module useRef - React Hook for referencing elements
 * @module useState - React Hook for managing state
 * @module play - SVG file for a play button
 * @module useEmblaCarousel - Embla Carousel React Hook
 */
import {Image, Link} from '@shopify/hydrogen';
import {useCallback, useEffect, useRef, useState} from 'react';
import play from '../../assets/image/play.svg';
import useEmblaCarousel from 'embla-carousel-react';
import '../../assets/css/banner.css';

/**
 * Banner component for displaying various types of banners, including small, dynamic, and full banners.
 * Provides functionalities for embedding videos, images, and handling responsiveness and carousel behavior.
 *
 * @param {Object} props - Properties passed to the component.
 * @param {Object} props.sanity - Sanity data related to the banner content and styles.
 * @param {string} props.classWidth - Custom width class to apply to the banner section.
 * @param {boolean} props.isColumn - Determines if the layout should be displayed in a column format.
 *
 * @returns {JSX.Element} The Banner component rendered as JSX.
 */
export default function Banner({sanity, classWidth, isColumn}) {
  /** State for selected index in the carousel */
  const [selectedIndex, setSelectedIndex] = useState(0); 
  /** State for video stream control */
  const [mystream, setmystream] = useState(null);
  /** State for video switch control */
  const [videoswitch, setvideo] = useState(true);
  /** State and initialization for Embla Carousel */
  const [viewportRef, embla] = useEmblaCarousel();
  /** State for scroll snaps */
  const [scrollSnaps, setScrollSnaps] = useState([]);
  /** State for previous button enabled state */
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  /** State for next button enabled state */
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  /** State for mobile responsiveness */
  const [isMobile, setisMobile] = useState(false);

  /** Function for handling scroll */
  const scrollTo = useCallback(
    (index) => embla && embla.scrollTo(index),
    [embla],
  );

/**
 * Reference to the video element.
 * @type {React.RefObject<HTMLVideoElement>}
 */
const video = useRef(null);
  
 /**
  * Handles the play functionality of the video. Toggles the video stream
  * between playing and paused states, enabling or disabling the video track as needed.
  */
  const handlePlay = () => {
    if (videoswitch) {
      setvideo(false);
      mystream.getTracks().forEach(function (track) {
        if (track.readyState === 'live' && track.kind === 'video') {
          track.enabled = false;
          track.play();
        }
      });
    } else {
      setvideo(true);
      mystream.getTracks().forEach(function (track) {
        if (track.readyState === 'live' && track.kind === 'video') {
          track.enabled = true;
        }
      });
    }
  };

 /**
  * Handles the mobile detection. Sets the state of 'isMobile' to true
  * if the window's inner width is less than 768.
  */
  const handleIsMobile = () => {
    if (window.innerWidth < 768) {
      setisMobile(true);
    } else {
      setisMobile(false);
    }
  };

  /**
   * Callback function that is triggered on carousel selection.
   * Updates the selected index and enables/disables navigation buttons based on the current position.
   */
  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla, setSelectedIndex]);

  /**
   * Checks and adjusts the responsiveness of an iframe.
   * Sets the width and height attributes of the iframe to ensure proper scaling within its container.
   */
  const checkResponsiveIframe = () => {
    const iframe = document.querySelector('iframe');
    if (iframe) {
      const iframeWidth = iframe.clientWidth;
      const iframeHeight = iframe.clientHeight;
      const iframeRatio = iframeWidth / iframeHeight;
      const container = iframe.parentElement;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const containerRatio = containerWidth / containerHeight;
      if (iframeRatio > containerRatio) {
        iframe.setAttribute('width', '100%');
        iframe.setAttribute('height', '100%');
        
      } else {
        iframe.setAttribute('width', '100%');
        iframe.setAttribute('height', '100%');
      }
    }
  };

  useEffect(() => {
    if (!embla) return;
    onSelect();
    setScrollSnaps(embla.scrollSnapList());
    embla.on('select', onSelect);
  }, [embla, setScrollSnaps, onSelect]);

  const toLowerCase = (string) => {
    let rstring = string;
    if (rstring) {
      rstring = rstring.toLowerCase();
      rstring = rstring.replace(/ /g, '_');
    }
    if (rstring.includes('-')) {
      rstring = rstring.replace(/-/g, '');
    }
    return rstring;
  };

  useEffect(() => {
    handleIsMobile();
  }, []);

  useEffect(() => {
    if (isMobile) {
      checkResponsiveIframe();
    }
  }, [isMobile]);

/**
 * Renders a complex component structure representing a configurable banner.
 *
 * @returns {JSX.Element} A div container that wraps the content:
 * - Outer Container: A <div> with class `h-full`.
 * - Type Conditions: Displays content based on `sanity.type` being "Banner Small" or "Banner Dinámico."
 * - Section Element: Inside the div, with various classes and styles determined by `sanity` object and `classWidth`.
 * - Title: Renders an <h2> tag with the title if `sanity.title` exists.
 * - List Rendering: Maps over `sanity.list` to create a slideshow-like component with the following types:
 *   - Embed Type: Displays embedded video.
 *   - Image Type: Displays an image wrapped in a link.
 *   - Video Type: Renders video player with optional play button functionality.
 *   - Dynamic Type: Renders customized section with image, title, description, and button wrapped in links, controlled by `isColumn` flag.
 * - Pagination Dots: Renders pagination dots if `sanity.list.length` is greater than 1 with different styling based on `selectedIndex`.
 */
  return (
    <div className="h-full">
      {(sanity.type == 'Banner Small' || sanity.type == 'Banner Dinámico') && (
        <section
          className={`section_librero section_info_banner h-full overflow-x-hidden ${classWidth} ${toLowerCase(
            sanity.type,
          )}`}
          style={{
            background:
              sanity.backgroundType == 'Solid'
                ? sanity.backgroundColor
                : sanity.backgroundType == 'Image'
                ? `url(${sanity.backgroundImage.asset.url})`
                : sanity.backgroundType == 'Gradient'
                ? `linear-gradient(90deg, ${sanity?.backgroundGradient.gradientFrom} 0%, ${sanity.backgroundGradient.gradientTo} 100%)`
                : '#00000',
            backgroundSize: 'cover',
          }}
          key={sanity._key}
        >
          <div className="md:container md:mx-auto flex flex-col h-full">
            {sanity.title && (
              <div className="mx-auto container">
                <h2 className="section-title font-bold py-5 text-3xl text-center">
                  {' '}
                  {sanity.title}{' '}
                </h2>
              </div>
            )}
            {sanity.list && (
              <div
                className="overflow-x-visible overflow-y-visible h-full"
                ref={viewportRef}
              >
                <div className="embla__container h-full">
                  {sanity.list
                    ? sanity.list.map((item) => {
                        if (item.bannerSmallType == 'Embed') {
                          return (
                            <>
                              <div
                                className="embla__slide w-full  mx-auto flex items-center justify-center informacion-slide"
                                key={Math.random()}
                              >
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: item.bannerSmallVideoEmbed,
                                  }}
                                  className="video-container"
                                />
                              </div>
                            </>
                          );
                        }
                        if (item.bannerSmallType == 'Image') {
                          return (
                            <div
                              className="embla__slide w-full flex items-center justify-center informacion-slide"
                              key={Math.random()}
                            >
                              <Link
                                className="w-6/12 xs:w-full"
                                to={
                                  item.page
                                    ? `categoria-producto/${item.page.slug.current}`
                                    : item.url
                                    ? item.url
                                    : ''
                                }
                                key={Math.random()}
                              >
                                <Image
                                  src={item.bannerSmallImage.asset.url}
                                  alt="Banner Image"
                                  width={'100%'}
                                  height={'auto'}
                                  className="xs:w-10/12 mx-auto rounded-2xl informacion-image-slider"
                                />
                              </Link>
                            </div>
                          );
                        }
                        if (item.bannerSmallType == 'Video') {
                          return (
                            <div
                              className="embla__slide w-full flex items-center justify-center informacion-slide"
                              key={Math.random()}
                            >
                              <div className="flex relative items-center justify-center h-full md:w-6/12 w-full ">
                                {!isMobile && (
                                  <button
                                    className={`absolute cursor-pointer z-10 top-1/3 left-1/2  ${
                                      !videoswitch ? 'hidden' : ''
                                    }`}
                                    onClick={handlePlay}
                                  >
                                    <Image
                                      src={play}
                                      width={50}
                                      height={50}
                                      alt="play"
                                    />
                                  </button>
                                )}
                                <video
                                  controls
                                  width={'100%'}
                                  height={'auto'}
                                  className="rounded-md mx-auto w-full"
                                  ref={video}
                                >
                                  <source
                                    src={item.bannerSmallVideo.asset.url}
                                    type="video/mp4"
                                  ></source>
                                </video>
                              </div>
                            </div>
                          );
                        }
                        if (item.bannerSmallType == 'Dynamic') {
                          return (
                            <div
                              className="embla__slide w-full flex items-center justify-center informacion-slide"
                              key={Math.random()}
                            >
                              <div
                                className={`rounded-xl p-5 min-h-max xs:py-8 xs:px-3 bg-purple-120 text-white flex gap-5 xs:gap-2 ${
                                  isColumn ? 'md:w-full' : 'md:w-6/12'
                                } xs:w-full mx-auto h-full`}
                                key={Math.random()}
                              >
                                <div className="w-5/12 xs:px-0 xs:w-5/12 xs:py-0 flex items-center">
                                  <Image
                                    src={item.bannerSmallDynamicImage.asset.url}
                                    width={'100%'}
                                    height={'auto'}
                                    alt="Banner"
                                    className="xs:w-10/12 mx-auto rounded-xl informacion-image"
                                  />
                                </div>
                                <div className="xs:w-7/12 w-7/12 px-4 flex items-start flex-col justify-center xs:px-0 xs:py-0">
                                  <h2 className="font-semibold text-2xl xs:text-lg leading-tight">
                                    {item.bannerSmallDynamicTitle}{' '}
                                  </h2>
                                  <p className="my-2 mb-3">
                                    {item.bannerSmallDynamicDescription}
                                  </p>
                                  <Link
                                    className="btn-main px-10 p-4 mt-1 xs:w-full xs:mt-2 rounded-full bg-white text-purple-120 disabled:bg-gray-400"
                                    to={
                                      item.page
                                        ? item.page?.slug
                                          ? `/${item.page.slug.current}`
                                          : `/`
                                        : item.url
                                        ? item.url
                                        : ''
                                    }
                                  >
                                    {item.bannerSmallButtonText}
                                  </Link>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      })
                    : ''}
                </div>
                {sanity.list.length > 1 && (
                  <div className="flex justify-center mt-5 ">
                    <div className="embla__dots">
                      {scrollSnaps.map((_, index) => (
                        <button
                          className={`bg-purple-120 w-10 rounded-full h-2 ml-1 ease-in 
                      ${index === selectedIndex ? 'w-10' : 'w-2 opacity-50'}
                  `}
                          key={index}
                          selected={index === selectedIndex}
                          onClick={() => scrollTo(index)}
                        >
                          {' '}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {sanity.type == 'Banner Full' && (
        <div key={sanity._key} className={`${toLowerCase(sanity.type)}`}>
          <div
            className="w-full bannerFull"
            style={{
              background: `url(${sanity.bannerFullBackgroundImage.asset.url})`,
            }}
          ></div>

          <div className="mx-auto p-10 w-6/12 text-center">
            <h2 className="font-bold text-2xl">{sanity.bannerFullText} </h2>
            <p> {sanity.bannerFullSubtext}</p>
          </div>
        </div>
      )}
    </div>
  );
}
